import { createAppRequest } from '@/services/request';
import { unwrapApiResponse, type ApiRequestOptions } from './types/common';
import type {
  ApiResult,
  ConfigDisablePayload,
  CreateSceneTemplatePayload,
  ExpenseStandardPolicyListQuery,
  ExpenseStandardPolicyPayload,
  ExpenseStandardPolicyRecord,
  PersonalQuotaConfigListQuery,
  PersonalQuotaConfigPayload,
  PersonalQuotaConfigRecord,
  SceneTemplateRecord,
  UpdateSceneTemplatePayload,
  V1ConfigPageQuery,
  V1ConfigPageResult,
} from './types/reimbursement';

const request = createAppRequest();

function unwrap<T>(response: ApiResult<T>): T {
  return unwrapApiResponse(response);
}

async function listAllPages<T>(
  loader: (pageNumber: number, pageSize: number) => Promise<V1ConfigPageResult<T>>,
) {
  const pageSize = 100;
  let pageNumber = 1;
  const items: T[] = [];
  let total = 0;
  do {
    const result = await loader(pageNumber, pageSize);
    items.push(...(result.items || []));
    total = result.total || 0;
    if (!result.items?.length) {
      break;
    }
    pageNumber += 1;
  } while (items.length < total);
  return items;
}

export async function listSceneTemplates(
  params?: V1ConfigPageQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<V1ConfigPageResult<SceneTemplateRecord>>>(
    '/bff/reimbursements/settings/scene-templates',
    { method: 'GET', params, ...(options || {}) },
  );
  return unwrap(response);
}

export async function listAllSceneTemplates(
  params?: Omit<V1ConfigPageQuery, 'pageNumber' | 'pageSize'>,
  options?: ApiRequestOptions,
) {
  return listAllPages((pageNumber, pageSize) =>
    listSceneTemplates({ ...params, pageNumber, pageSize }, options),
  );
}

export async function getSceneTemplate(
  uuid: string,
  expenseYearMonth?: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<SceneTemplateRecord>>(
    `/bff/reimbursements/settings/scene-templates/${uuid}`,
    { method: 'GET', params: { expenseYearMonth }, ...(options || {}) },
  );
  return unwrap(response);
}

export async function createSceneTemplate(
  body: CreateSceneTemplatePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<SceneTemplateRecord>>(
    '/bff/reimbursements/settings/scene-templates',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateSceneTemplate(
  uuid: string,
  body: UpdateSceneTemplatePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<SceneTemplateRecord>>(
    `/bff/reimbursements/settings/scene-templates/${uuid}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function enableSceneTemplate(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<SceneTemplateRecord>>(
    `/bff/reimbursements/settings/scene-templates/${uuid}/enable`,
    { method: 'POST', ...(options || {}) },
  );
  return unwrap(response);
}

export async function disableSceneTemplate(
  uuid: string,
  body: ConfigDisablePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<SceneTemplateRecord>>(
    `/bff/reimbursements/settings/scene-templates/${uuid}/disable`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listExpenseStandardPolicies(
  params?: ExpenseStandardPolicyListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<V1ConfigPageResult<ExpenseStandardPolicyRecord>>
  >('/bff/reimbursements/settings/expense-standard-policies', {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return unwrap(response);
}

export async function getExpenseStandardPolicy(
  uuid: string,
  expenseYearMonth?: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseStandardPolicyRecord>>(
    `/bff/reimbursements/settings/expense-standard-policies/${uuid}`,
    { method: 'GET', params: { expenseYearMonth }, ...(options || {}) },
  );
  return unwrap(response);
}

export async function createExpenseStandardPolicy(
  body: ExpenseStandardPolicyPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseStandardPolicyRecord>>(
    '/bff/reimbursements/settings/expense-standard-policies',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updateExpenseStandardPolicy(
  uuid: string,
  body: ExpenseStandardPolicyPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseStandardPolicyRecord>>(
    `/bff/reimbursements/settings/expense-standard-policies/${uuid}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function enableExpenseStandardPolicy(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseStandardPolicyRecord>>(
    `/bff/reimbursements/settings/expense-standard-policies/${uuid}/enable`,
    { method: 'POST', ...(options || {}) },
  );
  return unwrap(response);
}

export async function disableExpenseStandardPolicy(
  uuid: string,
  body: ConfigDisablePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<ExpenseStandardPolicyRecord>>(
    `/bff/reimbursements/settings/expense-standard-policies/${uuid}/disable`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function listPersonalQuotaConfigs(
  params?: PersonalQuotaConfigListQuery,
  options?: ApiRequestOptions,
) {
  const response = await request<
    ApiResult<V1ConfigPageResult<PersonalQuotaConfigRecord>>
  >('/bff/reimbursements/settings/personal-quota-configs', {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return unwrap(response);
}

export async function getPersonalQuotaConfig(
  uuid: string,
  expenseYearMonth?: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PersonalQuotaConfigRecord>>(
    `/bff/reimbursements/settings/personal-quota-configs/${uuid}`,
    { method: 'GET', params: { expenseYearMonth }, ...(options || {}) },
  );
  return unwrap(response);
}

export async function createPersonalQuotaConfig(
  body: PersonalQuotaConfigPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PersonalQuotaConfigRecord>>(
    '/bff/reimbursements/settings/personal-quota-configs',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function updatePersonalQuotaConfig(
  uuid: string,
  body: PersonalQuotaConfigPayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PersonalQuotaConfigRecord>>(
    `/bff/reimbursements/settings/personal-quota-configs/${uuid}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}

export async function enablePersonalQuotaConfig(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PersonalQuotaConfigRecord>>(
    `/bff/reimbursements/settings/personal-quota-configs/${uuid}/enable`,
    { method: 'POST', ...(options || {}) },
  );
  return unwrap(response);
}

export async function disablePersonalQuotaConfig(
  uuid: string,
  body: ConfigDisablePayload,
  options?: ApiRequestOptions,
) {
  const response = await request<ApiResult<PersonalQuotaConfigRecord>>(
    `/bff/reimbursements/settings/personal-quota-configs/${uuid}/disable`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
  return unwrap(response);
}
